import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { Radio } from "ant-design-vue";
import { ListStates } from "@/util/enum";
import classNames from "classnames";
import { ListStateLocale } from "@/locales/localeid";
import { AuthService, fmtMsg, GLUtil } from "commonui";

export interface ListStatesRadiosProps {
	value?: ListStates;
	allCnt?: number;
	activeCnt?: number;
	rwAssignedCount?: number;
	vsAssignedCount?: number;
	exposureCount?: number;
	inActiveCnt?: number;
	pendingCount?: number;
	futureCount?: number;
	onChange?: (value: ListStates) => void;
	openGroupMode?: (e) => void;
	showGroupingButton?: boolean;
	showActiveRadio?: boolean;
	showInActiveRadio?: boolean;
	className?: string;
	rmQueuedCount?: number;
	rmScheduledCount?: number;
	rmProcessingCount?: number;
	rmDeletedCount?: number;
}

@Component({
	components: {
		Radio,
		RadioGroup: Radio.Group
	}
})
export default class ListStateRadios extends Vue {
	@Prop() value?: any;
	@Prop() allCnt!: number;
	@Prop() activeCnt!: number;
	@Prop() inActiveCnt!: number;
	@Prop() pendingCount!: number;
	@Prop() futureCount!: number;
	@Prop() rwAssignedCount!: number;
	@Prop() vsAssignedCount!: number;
	@Prop() exposureCount!: number;
	@Prop() onChange!: (value: ListStates) => void;
	@Prop() openGroupMode?: (e) => void;
	@Prop() showGroupingButton?: boolean;
	@Prop({ default: true }) showInActiveRadio?: boolean;
	@Prop({ default: true }) showActiveRadio?: boolean;
	@Prop() className?: string;
	@Prop() rmQueuedCount!: number;
	@Prop() rmScheduledCount!: number;
	@Prop() rmProcessingCount!: number;
	@Prop() rmDeletedCount!: number;

	get mergedClass() {
		return classNames("list-states", this.className);
	}

	get listStates() {
		return ListStates;
	}

	onListChange(e: any) {
		this.onChange(parseInt(e.target.value));
	}

	localizedString(id: string, params?: {}) {
		return fmtMsg(id, params);
	}

	get listStateLocale() {
		return ListStateLocale;
	}

	get isExposureCountVisible() {
		return typeof this.exposureCount === "number";
	}

	get isRWAssignedCountVisible() {
		return typeof this.rwAssignedCount === "number";
	}

	get isVSAssignedCountVisible() {
		return typeof this.vsAssignedCount === "number";
	}

	get isPendingCountVisible() {
		return typeof this.pendingCount === "number";
	}

	get isFutureCountVisible() {
		return typeof this.futureCount === "number";
	}

	get isRMQueuedCountVisible() {
		return typeof this.rmQueuedCount === "number";
	}

	get isRMScheduledCountVisible() {
		return typeof this.rmScheduledCount === "number";
	}

	get isRMProcessingCountVisible() {
		return typeof this.rmProcessingCount === "number";
	}

	get isRMDeletedCountVisible() {
		return typeof this.rmDeletedCount === "number";
	}

	get inActiveCount() {
		return typeof this.inActiveCnt === "number"
			? this.inActiveCnt
			: this.allCnt - this.activeCnt - (this.futureCount || 0);
	}

	renderState(text: string, cnt: number) {
		const localizedValue = this.localizedString(text);
		const countValue = cnt || 0;
		return `<span>${localizedValue}</span>
                <span class="cnt">(${countValue})</span>`;
	}
}

export class ListStatesStore {
	constructor(private storeKey, private stateKey, private userKey?) {
		var userSession = AuthService.getSessionFromStorage();
		this.userKey = userKey || (userSession ? JSON.parse(userSession).profile.sub : storeKey);
	}
	private get unionKey(): string {
		return `${this.userKey}_${this.storeKey}`;
	}

	setStateKey(key) {
		if (!GLUtil.isNullOrUndefined(key)) {
			this.stateKey = key;
		}
	}
	getStates(id?) {
		id = id || this.stateKey;
		const states = JSON.parse(sessionStorage.getItem(this.unionKey) || "{}");
		return { id, states };
	}
	getState(id?, defaultState?) {
		const { id: key, states } = this.getStates(id);
		return GLUtil.isNullOrUndefined(states[key]) ? defaultState || ListStates.Active : states[key];
	}
	setState(state) {
		const { id, states } = this.getStates();
		states[id] = state;
		sessionStorage.setItem(this.unionKey, JSON.stringify(states));
	}
	getUrlQuery(page) {
		page.disabled = ListStatesStore.toDisabled(this.getState());
		page.future = this.getState() === ListStates.Future ? true : null;
		page.includePendingInvite =
			this.getState() === ListStates.All || this.getState() === ListStates.Pending ? true : null;
		page.includeOnlyPending = this.getState() === ListStates.Pending ? true : null;
		return page;
	}
	static getStateCnt(
		total,
		active,
		{ listState: state },
		futureCount = 0,
		pendingCount = 0,
		inActiveCnt = null,
		exposureCount = 0,
		rwAssignedCount = 0,
		vsAssignedCount = 0,
		rmQueuedCount = 0,
		rmScheduledCount = 0,
		rmProcessingCount = 0,
		rmDeletedCount = 0
	) {
		console.log("state");
		switch (state) {
			case ListStates.Active:
				return active;
			case ListStates.Inactive:
				return inActiveCnt && typeof inActiveCnt === "number" ? inActiveCnt : total - active;
			case ListStates.Pending:
				return pendingCount;
			case ListStates.Future:
				return futureCount;
			case ListStates.Exposure:
				return exposureCount;
			case ListStates.RWAssigned:
				return rwAssignedCount;
			case ListStates.VSAssigned:
				return vsAssignedCount;
			case ListStates.Queued:
				return rmQueuedCount;
			case ListStates.Scheduled:
				return rmScheduledCount;
			case ListStates.Processing:
				return rmProcessingCount;
			case ListStates.Deleted:
				return rmDeletedCount;
			default:
				return total;
		}
	}
	static toDisabled(state) {
		return state === ListStates.All ||
			GLUtil.isNullOrUndefined(state) ||
			/*state === ListStates.Promotion ||*/ state === ListStates.Pending ||
			state === ListStates.Future
			? null
			: !!state;
	}
}
